import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type DragEvent,
  type KeyboardEvent,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { EmptyState, InlineError, Input, Menu, Skeleton } from '../../components/ui';
import { PageHeader } from '../../layouts/Shells';
import { PanelFold } from '../listView/PanelFold';
import { TaskDetailsPanel } from '../listView/TaskDetailsPanel';
import { TASK_DRAG_MIME, formatDate, type TaskLookups } from '../listView/TaskTable';
import { taskMatchesSearch } from '../listView/taskFilters';
import { usePersonalProject, useProjectWorkspace } from '../projects/hooks';
import {
  taskKeys,
  useCreateTask,
  useMoveTaskStatus,
  useProjectTasks,
  useSetTaskCompleted,
  useTaskDefaults,
} from '../tasks/hooks';
import type { ProjectStatus } from '../projects/types';
import {
  huddleStatusChoices,
  huddleStatusOf,
  huddleToggleIntent,
  type HuddleStatusChoice,
  type HuddleStatusIntent,
} from './huddleStatus';
import { taskErrorMessage } from '../tasks/queries';
import {
  CONSOLE_GROUP_LABELS,
  CONSOLE_GROUP_ORDER,
  PARKED_LABEL,
  TRIP_LABEL,
  consoleViewGroupOf,
  consoleShownTitle,
  tripPlacement,
  withParkedPrefix,
  withTripPrefix,
  type ConsoleViewGroup,
} from '../huddlePortal/consoleGroup';
import { consoleSection, type ConsoleTab } from '../huddlePortal/consoleView';
import {
  TASK_PRIORITY_LABELS,
  nextTaskPosition,
  type ProjectTasks,
  type Task,
} from '../tasks/types';
import {
  useAddMyDaySection,
  useDeleteMyDaySection,
  useMoveMyDaySections,
  useMyDaySections,
  usePlaceTask,
  usePlaceTasks,
  useRenameMyDaySection,
} from './hooks';
import {
  EMPTY_LIST,
  RELEASE,
  buildMyDay,
  cleanSectionName,
  completedOn,
  completionLabel,
  dropIndex,
  headerDate,
  makeRoomAtTop,
  nextSectionPosition,
  placement,
  plannedDay,
  reorderSections,
  sortSections,
  reorderWrites,
  todayIso,
  type MyDayColumn,
  type MyDayList,
  type MyDaySectionRow,
} from './myDayLens';
import './myDay.css';
import { HuddleLinksPanel } from './HuddleLinksPanel';
import { ConsoleChatPanel } from './ConsoleChatPanel';
import { getHuddleLink } from './huddleLinks';
import { useAuth } from '../auth/AuthProvider';
import { useMarkTaskHuddleCommentsRead, useUnreadHuddleComments } from '../notifications/hooks';
import {
  huddleCommentLabel,
  huddleLatestTime,
  huddleUnreadForSection,
  isHuddleSectionName,
  type HuddleUnreadSummary,
} from './huddleUnread';

/**
 * My Day — your own tasks, arranged for today.
 *
 * docs/design-note-my-day.md. This is the personal project seen through a
 * different lens: the same rows Personal Tasks showed, read through
 * useProjectTasks so every write made here or in the task panel lands in one
 * cache. Two fixed lists, then any the person made themselves
 * (docs/design-note-my-day-sections.md), plus Unfinished, which is not a place
 * anything is moved to but a reading of dates. The header shows today and is
 * not a control: Kyu dropped the date picker so that nothing can be hidden by
 * looking at a different day.
 */

const UNFINISHED_LABEL = 'Unfinished';

/** A stable empty list for sections that have no Completed fold of their own. */
const EMPTY_TASKS: Task[] = [];

/** Move up (-1), Move down (1), or Move to top. */
type Step = -1 | 1 | 'top';

/** My Day's TAP Console header tabs: All first, so nothing hides on first load. */
const CONSOLE_TABS: readonly { key: ConsoleTab; label: string }[] = [
  { key: 'all', label: 'All' },
  ...CONSOLE_GROUP_ORDER.map((key) => ({ key, label: CONSOLE_GROUP_LABELS[key] })),
];
/**
 * The trip tab and Parked always show on My Day (Kyu, 2 Nov), so a new trip or
 * a parked task can be started from the tab. Alex's page shows the trip only
 * while a trip task is open, and never shows Parked.
 */
const TRIP_TAB: { key: ConsoleTab; label: string } = { key: 'trip', label: TRIP_LABEL };
const PARKED_TAB: { key: ConsoleTab; label: string } = { key: 'parked', label: PARKED_LABEL };

export function MyDayPage() {
  const personal = usePersonalProject();
  const workspace = useProjectWorkspace(personal.data);
  const projectId = personal.data ?? '';
  const projectTasks = useProjectTasks(personal.data);
  const sections = useMyDaySections();
  const today = todayIso();

  if (
    personal.isPending ||
    (personal.isSuccess && (workspace.isPending || projectTasks.isPending || sections.isPending))
  ) {
    return (
      <>
        <PageHeader title="My Day" topbar description={headerDate()} />
        <Skeleton lines={6} label="Loading your day" />
      </>
    );
  }

  if (
    personal.isError ||
    workspace.isError ||
    projectTasks.isError ||
    sections.isError ||
    !workspace.data ||
    !projectTasks.data ||
    !sections.data
  ) {
    return (
      <>
        <PageHeader title="My Day" topbar description={headerDate()} />
        <EmptyState
          title="Your day could not be loaded"
          description="Try again in a moment. If this keeps happening, tell an admin."
        />
      </>
    );
  }

  return (
    <MyDayBoardView
      projectId={projectId}
      projectName={workspace.data.project.name}
      lookups={{ members: workspace.data.members, statuses: workspace.data.statuses }}
      tasks={projectTasks.data.tasks}
      subtasksByParent={projectTasks.data.subtasksByParent}
      sections={sections.data}
      today={today}
    />
  );
}

function MyDayBoardView({
  projectId,
  projectName,
  lookups,
  tasks,
  subtasksByParent,
  sections,
  today,
}: {
  projectId: string;
  projectName: string;
  lookups: TaskLookups;
  tasks: Task[];
  subtasksByParent: Record<string, Task[]>;
  sections: MyDaySectionRow[];
  today: string;
}) {
  const { client } = useAuth();
  const [query, setQuery] = useState('');
  const searching = query.trim() !== '';
  // Search is a lens over the loaded list, as it is on the List View: it
  // narrows what is drawn and writes nothing. While it is on, dragging is off,
  // because a slot is an index into the WHOLE list and a drop computed against
  // a narrowed one would land somewhere the person cannot see.
  const shown = searching ? tasks.filter((task) => taskMatchesSearch(task, query)) : tasks;
  // get_huddle_link is deliberately owner-only. A section called TAP Console
  // is not enough to move it or expose the private DM on somebody else's day.
  const namedConsole = sections.find((section) => isHuddleSectionName(section.name));
  const [liveHuddleSectionId, setLiveHuddleSectionId] = useState<string | null>(null);
  useEffect(() => {
    let current = true;
    if (namedConsole === undefined) {
      setLiveHuddleSectionId(null);
      return () => {
        current = false;
      };
    }
    void getHuddleLink(client, namedConsole.id)
      .then((token) => {
        if (current) setLiveHuddleSectionId(token === null ? null : namedConsole.id);
      })
      .catch(() => {
        if (current) setLiveHuddleSectionId(null);
      });
    return () => {
      current = false;
    };
  }, [client, namedConsole?.id]);
  const board = buildMyDay(shown, today, sections, liveHuddleSectionId);
  const queryClient = useQueryClient();
  const place = usePlaceTask(projectId);
  const placeMany = usePlaceTasks(projectId);
  const createTask = useCreateTask(projectId);
  const complete = useSetTaskCompleted(projectId);
  const moveStatus = useMoveTaskStatus(projectId);
  const defaults = useTaskDefaults();
  const addSection = useAddMyDaySection();
  const renameSection = useRenameMyDaySection();
  const moveSections = useMoveMyDaySections();
  const removeSection = useDeleteMyDaySection(projectId);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedCommentId, setSelectedCommentId] = useState<string | null>(null);
  const closeDetails = useCallback(() => {
    setSelectedId(null);
    setSelectedCommentId(null);
  }, []);

  const alexHuddle = board.columns.find(
    (column) => column.custom && isHuddleSectionName(column.label),
  );
  const unreadHuddle = useUnreadHuddleComments(alexHuddle !== undefined);
  const markHuddleRead = useMarkTaskHuddleCommentsRead();
  const huddleSummary =
    alexHuddle === undefined
      ? null
      : huddleUnreadForSection(unreadHuddle.data ?? [], tasks, alexHuddle.key);

  const report = (failure: Error) => setError(taskErrorMessage(failure.message));

  /**
   * The board as the cache holds it NOW, not as it was last drawn. Two quick
   * key presses can both land before React draws the first move; the first
   * move's optimistic patch is already in the cache, so reading it here makes
   * the second press start from the moved order (Dev4 B1). Built from every
   * task, not the search lens: a slot is an index into the WHOLE list, and
   * while searching only "Move to <list>" can reach this.
   */
  function liveBoard() {
    const cached = queryClient.getQueryData<ProjectTasks>(taskKeys.project(projectId));
    return buildMyDay(cached?.tasks ?? tasks, today, sections);
  }

  /**
   * Put a task at a final index among the open rows of a column. The moved
   * task's write is its full placement (section and planned date move with
   * it, which lifts a task out of Unfinished); any neighbour the helper has
   * to pin gets its slot only.
   */
  function placeAt(key: string, taskId: string, finalIndex: number) {
    const live = liveBoard();
    const column = live.columns.find((candidate) => candidate.key === key);
    // A column that is not on the page cannot be dropped into. This can only
    // happen if a section was deleted on another device between the drag
    // starting and the drop landing, and doing nothing is the right answer:
    // the refetch that removes the column is already on its way.
    if (column === undefined) return;
    const list = live.lists[key] ?? EMPTY_LIST;
    const incoming = live.unfinished
      .concat(...Object.values(live.lists).map((other) => other.tasks))
      .find((task) => task.id === taskId && !task.is_completed);
    const writes = reorderWrites(list, taskId, finalIndex, incoming);
    const [moved, ...neighbours] = writes;
    if (moved === undefined) return;
    setError('');
    placeMany.mutate(
      [
        { taskId: moved.id, changes: placement(column, moved.position, today) },
        ...neighbours.map((write) => ({
          taskId: write.id,
          changes: { my_day_position: write.position },
        })),
      ],
      { onError: report },
    );
  }

  /**
   * A drop, from any list including Unfinished, into one of the columns. A
   * drop on a row is that row's index and a drop on the body is the end; in
   * the same list the index goes through dropIndex, because the dragged row
   * leaves its spot (Dev4 B2).
   */
  function dropInto(key: string, taskId: string, targetIndex: number) {
    const list = liveBoard().lists[key] ?? EMPTY_LIST;
    const inList = list.tasks.slice(0, list.openCount).some((task) => task.id === taskId);
    placeAt(key, taskId, inList ? dropIndex(list, taskId, targetIndex) : targetIndex);
  }

  function moveTo(task: Task, key: string) {
    // From the row menu rather than a drag: goes to the top of that list, and
    // is held there so the move reads as deliberate.
    dropInto(key, task.id, 0);
  }

  /**
   * Move up, Move down, Move to top, from the row menu or Alt+Arrow. The step
   * is taken among the rows the TAP Console tab shows (Kyu, 30 Sept r2): the
   * task lands at the slot of the shown row it passes, so rows of other
   * groups keep their order. On All, and on every other list, that is every
   * open row.
   */
  function reorder(key: string, task: Task, step: Step, tab: ConsoleTab) {
    const list = liveBoard().lists[key] ?? EMPTY_LIST;
    const open = list.tasks.slice(0, list.openCount);
    const shown = consoleSection(open, EMPTY_TASKS, tab).open;
    const from = shown.findIndex((row) => row.id === task.id);
    if (from === -1) return;
    const target = shown[step === 'top' ? 0 : from + step];
    if (target === undefined || target.id === task.id) return;
    placeAt(key, task.id, open.indexOf(target));
  }

  function release(task: Task) {
    setError('');
    place.mutate({ taskId: task.id, changes: RELEASE }, { onError: report });
  }

  function toggle(task: Task, isCompleted: boolean) {
    setError('');
    complete.mutate({ taskId: task.id, isCompleted }, { onError: report });
  }

  /**
   * The TAP Console status picker, and the checkbox on those rows. Status and
   * completion go in one write through the Board's optimistic mutation, so
   * My Day and Alex's page read the same pair. See huddleStatus.ts.
   */
  function setHuddleStatus(task: Task, intent: HuddleStatusIntent) {
    if (task.status_id === intent.statusId && task.is_completed === intent.isCompleted) return;
    setError('');
    moveStatus.mutate(
      { taskId: task.id, statusId: intent.statusId, isCompleted: intent.isCompleted },
      { onError: report },
    );
  }

  function toggleHuddle(task: Task, isCompleted: boolean) {
    const intent = huddleToggleIntent(task, isCompleted, lookups.statuses);
    if (intent === null) toggle(task, isCompleted);
    else setHuddleStatus(task, intent);
  }

  /**
   * The TAP Console group select (Kyu, 30 Sept). One column through the same
   * optimistic update a drop uses (usePlaceTask), so the row does not wait for
   * the refetch. Others writes 'others', not null: the select is an explicit
   * choice, and both read as Others everywhere (consoleGroupOf, read_huddle).
   */
  function setConsoleGroup(task: Task, choice: ConsoleViewGroup) {
    const changes = tripPlacement(task, choice);
    if (changes === null) return;
    setError('');
    place.mutate({ taskId: task.id, changes }, { onError: report });
  }

  // Done TAP Console rows live in that section's own Completed fold, so the
  // page-wide fold leaves them out rather than listing them twice.
  const consoleDoneEarlier =
    alexHuddle === undefined
      ? []
      : board.doneEarlier.filter((task) => task.my_day_section_id === alexHuddle.key);
  const pageDoneEarlier =
    consoleDoneEarlier.length === 0
      ? board.doneEarlier
      : board.doneEarlier.filter((task) => task.my_day_section_id !== alexHuddle?.key);

  /**
   * A task typed into a list lands on top of it, and stays there (Kyu, 5 Oct:
   * "it randomly appears somewhere in the middle of the long list"; 7 Oct:
   * "whenever I add a task, it would always go on top. Always.").
   *
   * It is inserted already held at slot 0, so it is on top from the insert
   * itself. Then every other held row in the list moves down one slot
   * (makeRoomAtTop), so slot 0 is the new task's alone: a later note on the
   * old top row can no longer win the slot back. mutateAsync, not per-call
   * callbacks: TanStack drops those for all but the last mutate() on a hook,
   * so two quick adds lost the first one's placement (Dev3 r2).
   */
  function add(column: MyDayColumn, title: string) {
    setError('');
    createTask
      .mutateAsync({
        projectId,
        title,
        sectionId: null,
        statusId: null,
        assigneeId: null,
        priority: null,
        dueDate: defaults.defaultDueDate,
        position: nextTaskPosition(tasks),
        myDay: {
          section: column.custom ? null : (column.key as 'priorities' | 'today'),
          sectionId: column.custom ? column.key : null,
          plannedOn: today,
          position: 0,
        },
      })
      .then((created) => {
        queryClient.setQueryData<ProjectTasks>(taskKeys.project(projectId), (cached) =>
          cached === undefined || cached.tasks.some((task) => task.id === created.id)
            ? cached
            : { ...cached, tasks: [...cached.tasks, created] },
        );
        const list = liveBoard().lists[column.key] ?? EMPTY_LIST;
        const room = makeRoomAtTop(list, created.id);
        if (room.length === 0) return;
        placeMany.mutate(
          room.map((write) => ({ taskId: write.id, changes: { my_day_position: write.position } })),
          { onError: report },
        );
      }, report);
  }

  function createSection(raw: string) {
    const name = cleanSectionName(raw);
    if (name === null) return;
    setError('');
    addSection.mutate({ name, position: nextSectionPosition(sections) }, { onError: report });
  }

  function rename(id: string, raw: string) {
    const name = cleanSectionName(raw);
    if (name === null) return;
    setError('');
    renameSection.mutate({ id, name }, { onError: report });
  }

  function shift(id: string, direction: -1 | 1) {
    const moves = reorderSections(sections, id, direction);
    if (moves.length === 0) return;
    setError('');
    moveSections.mutate(moves, { onError: report });
  }

  function remove(id: string) {
    setError('');
    removeSection.mutate(id, { onError: report });
  }

  const selected = tasks.find((task) => task.id === selectedId);
  const description = `${headerDate()} · ${board.openTotal} open, ${board.doneTodayTotal} done today`;

  function openTask(task: Task) {
    const unread = huddleSummary?.byTask[task.id];
    setSelectedId(task.id);
    setSelectedCommentId(unread?.newestCommentId ?? null);
    if (unread) {
      setError('');
      markHuddleRead.mutate(task.id, { onError: report });
    }
  }

  function openNewestHuddleComment() {
    const task = tasks.find((candidate) => candidate.id === huddleSummary?.newestTaskId);
    if (task) openTask(task);
  }

  return (
    <>
      <PageHeader title="My Day" topbar description={description} />
      <div className="my-day-toolbar">
        <div className="my-day-search">
          <Input
            label="Search my day"
            value={query}
            placeholder="Title, description or remarks"
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
        {searching ? (
          <span className="my-day-search-note">
            {shown.length === 1 ? '1 task matches' : `${shown.length} tasks match`} · clear the
            search to drag
          </span>
        ) : null}
      </div>
      {error === '' ? null : <InlineError>{error}</InlineError>}
      <div className="my-day-grid">
        {board.columns.map((column) => {
          const customIndex = board.columns.filter((candidate) => candidate.custom).indexOf(column);
          const storedIndex = sortSections(sections).findIndex(
            (section) => section.id === column.key,
          );
          const isOwnerConsole = column.key === liveHuddleSectionId;
          const card = (
            <ListCard
              key={column.key}
              column={column}
              /* Colour by position among the custom ones, cycling a short set of
               existing state tokens. The built-ins keep the two they had. */
              tone={column.custom ? `custom-${String(customIndex % 4)}` : column.key}
              list={board.lists[column.key] ?? EMPTY_LIST}
              columns={board.columns}
              canDrag={!searching}
              onDrop={(taskId, at) => dropInto(column.key, taskId, at)}
              onReorder={(task, step, tab) => reorder(column.key, task, step, tab)}
              onAdd={(title) => add(column, title)}
              onOpen={openTask}
              onToggle={toggle}
              onMove={moveTo}
              onRelease={release}
              onRename={column.custom ? (name) => rename(column.key, name) : null}
              onShift={column.custom ? (direction) => shift(column.key, direction) : null}
              onDelete={column.custom ? () => remove(column.key) : null}
              atStart={column.custom && storedIndex === 0}
              atEnd={column.custom && storedIndex === sections.length - 1}
              huddleUnread={column.key === alexHuddle?.key ? huddleSummary : null}
              onOpenNewestHuddle={openNewestHuddleComment}
              statuses={lookups.statuses}
              onSetHuddleStatus={setHuddleStatus}
              onToggleHuddle={toggleHuddle}
              onSetConsoleGroup={setConsoleGroup}
              doneEarlier={column.key === alexHuddle?.key ? consoleDoneEarlier : EMPTY_TASKS}
            />
          );
          return isOwnerConsole ? (
            <div className="my-day-console-row" key={column.key}>
              {card}
              <ConsoleChatPanel />
            </div>
          ) : (
            card
          );
        })}
        <AddSectionCard onAdd={createSection} />
        <UnfinishedCard
          tasks={board.unfinished}
          columns={board.columns}
          canDrag={!searching}
          onOpen={openTask}
          onToggle={toggle}
          onMove={moveTo}
        />
      </div>
      <DoneEarlier
        doneEarlier={pageDoneEarlier}
        columns={board.columns}
        onOpen={openTask}
        onToggle={toggle}
      />

      {selected ? (
        <TaskDetailsPanel
          // Same key, same reason as ListView: My Day switches tasks with
          // the panel open too.
          key={selected.id}
          task={selected}
          subtasks={subtasksByParent[selected.id] ?? []}
          lookups={lookups}
          projectId={projectId}
          projectName={projectName}
          canEdit
          canManage
          compact
          // The row here carries no editors, so the panel is where a My Day
          // task gets its due date, priority, status and assignee.
          fieldsEditable
          highlightCommentId={selectedCommentId}
          ensureAssignable={(_userId, then) => then()}
          onClose={closeDetails}
        />
      ) : null}
    </>
  );
}

/* ── One list: a built-in, or one the person made ───────────────────────── */

function ListCard({
  column,
  tone,
  list,
  columns,
  canDrag,
  onDrop,
  onReorder,
  onAdd,
  onOpen,
  onToggle,
  onMove,
  onRelease,
  onRename,
  onShift,
  onDelete,
  atStart,
  atEnd,
  huddleUnread,
  onOpenNewestHuddle,
  statuses,
  onSetHuddleStatus,
  onToggleHuddle,
  onSetConsoleGroup,
  doneEarlier,
}: {
  column: MyDayColumn;
  /** Class suffix for the header colour. Never the raw key: a section id is a
      uuid and would make a class name nobody can style. */
  tone: string;
  list: MyDayList;
  /** Every column on the page, for the row menu's "Move to …". */
  columns: MyDayColumn[];
  /** Off while a search narrows the list; see MyDayBoardView. */
  canDrag: boolean;
  onDrop: (taskId: string, index: number) => void;
  /** Move up, Move down, Move to top, among the rows the tab shows. Off
      while searching, with dragging. */
  onReorder: (task: Task, step: Step, tab: ConsoleTab) => void;
  onAdd: (title: string) => void;
  onOpen: (task: Task) => void;
  onToggle: (task: Task, isCompleted: boolean) => void;
  onMove: (task: Task, key: string) => void;
  onRelease: (task: Task) => void;
  /** Null on the two built-ins: they are not editable. */
  onRename: ((name: string) => void) | null;
  onShift: ((direction: -1 | 1) => void) | null;
  onDelete: (() => void) | null;
  atStart: boolean;
  atEnd: boolean;
  /** Present only for the custom section named TAP Console (or, before the
      rename, Alex Huddle); see isHuddleSectionName. */
  huddleUnread: HuddleUnreadSummary | null;
  onOpenNewestHuddle: () => void;
  /** The personal project's statuses, for the TAP Console status picker. */
  statuses: ProjectStatus[];
  onSetHuddleStatus: (task: Task, intent: HuddleStatusIntent) => void;
  /** The checkbox on a TAP Console row, which keeps the picker in step. */
  onToggleHuddle: (task: Task, isCompleted: boolean) => void;
  /** The TAP Console group select. */
  onSetConsoleGroup: (task: Task, choice: ConsoleViewGroup) => void;
  /** This section's rows completed before today; only the TAP Console gets any. */
  doneEarlier: Task[];
}) {
  const { client } = useAuth();
  const [over, setOver] = useState(false);
  const [draft, setDraft] = useState('');
  const [renaming, setRenaming] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [huddleOpen, setHuddleOpen] = useState(false);
  const [huddleToken, setHuddleToken] = useState<string | null>(null);
  const [consoleTab, setConsoleTab] = useState<ConsoleTab>('all');
  const consoleTabs = [...CONSOLE_TABS, PARKED_TAB, TRIP_TAB];
  const label = column.label;

  useEffect(() => {
    if (!column.custom) return;
    let current = true;
    void getHuddleLink(client, column.key)
      .then((token) => {
        if (current) setHuddleToken(token);
      })
      .catch(() => {
        if (current) setHuddleToken(null);
      });
    return () => {
      current = false;
    };
  }, [client, column.custom, column.key]);

  const huddleInHeader = column.custom && (isHuddleSectionName(label) || huddleToken !== null);
  // The status picker lives on the same section as the Huddle link button:
  // the one Alex's page reads. Every other list keeps its lean row.
  const statusPicker = huddleInHeader
    ? {
        choices: huddleStatusChoices(statuses),
        statuses,
        onSet: onSetHuddleStatus,
        onSetGroup: onSetConsoleGroup,
        tripEnabled: true,
      }
    : null;
  // The TAP Console section: open rows in the list, done ones (today's and
  // earlier) in its own fold, both narrowed by the header tab. Dragging and
  // Move up, Move down, Move to top work inside a tab too (Kyu, 30 Sept r2):
  // a drop on a row goes to that row's index in the WHOLE list, and a move
  // steps past the next row the tab shows (reorder), so rows of other groups
  // keep their order.
  const consoleRows =
    statusPicker === null ? null : consoleSection(list.tasks, doneEarlier, consoleTab);
  const rows = consoleRows === null ? list.tasks : consoleRows.open;
  const openShown = consoleRows === null ? list.openCount : consoleRows.open.length;

  function accepts(event: DragEvent) {
    return canDrag && event.dataTransfer.types.includes(TASK_DRAG_MIME);
  }

  function submit(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key !== 'Enter') return;
    const title = draft.trim();
    if (title === '') return;
    event.preventDefault();
    // The trip tab saves what is typed with "Trip: " in front (Kyu, 13 Oct),
    // and Parked with "Parked: " (Kyu, 2 Nov).
    onAdd(
      consoleRows !== null && consoleTab === 'trip'
        ? withTripPrefix(title)
        : consoleRows !== null && consoleTab === 'parked'
          ? withParkedPrefix(title)
          : title,
    );
    setDraft('');
  }

  const menuItems = [
    ...(onRename === null ? [] : [{ label: 'Rename', onSelect: () => setRenaming(label) }]),
    ...(onShift === null || atStart ? [] : [{ label: 'Move left', onSelect: () => onShift(-1) }]),
    ...(onShift === null || atEnd ? [] : [{ label: 'Move right', onSelect: () => onShift(1) }]),
    ...(onDelete === null
      ? []
      : [{ label: 'Delete section', onSelect: () => setConfirming(true) }]),
    ...(column.custom && !huddleInHeader
      ? [
          {
            label: huddleOpen ? 'Hide huddle link' : 'Huddle link',
            onSelect: () => setHuddleOpen((open) => !open),
          },
        ]
      : []),
  ];

  return (
    <section
      className={`my-day-list my-day-list--${tone}${over ? ' my-day-list--over' : ''}`}
      aria-label={label}
    >
      <header className="my-day-list-head">
        {renaming === null ? (
          <h2 className="my-day-list-title">{label}</h2>
        ) : (
          /* Renamed in place rather than in a dialog. The name is one short
             string and the person is looking straight at it. */
          <input
            className="my-day-rename"
            type="text"
            autoFocus
            maxLength={40}
            aria-label={`Rename ${label}`}
            value={renaming}
            onChange={(event) => setRenaming(event.target.value)}
            onBlur={() => setRenaming(null)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') setRenaming(null);
              if (event.key !== 'Enter' || onRename === null) return;
              event.preventDefault();
              onRename(renaming);
              setRenaming(null);
            }}
          />
        )}
        <span className="my-day-list-count">{completionLabel(list)}</span>
        {huddleInHeader ? (
          <button
            type="button"
            className="my-day-huddle-button"
            aria-expanded={huddleOpen}
            onClick={() => setHuddleOpen((open) => !open)}
          >
            Huddle link
          </button>
        ) : null}
        {menuItems.length === 0 ? null : (
          <Menu
            trigger={<span aria-hidden="true">…</span>}
            triggerLabel={`Actions for ${label}`}
            items={menuItems}
          />
        )}
      </header>
      {consoleRows === null ? null : (
        <div
          className="lead-subtabs my-day-console-tabs"
          role="tablist"
          aria-label={`${label} groups`}
        >
          {consoleTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={consoleTab === tab.key}
              className={`lead-subtab${consoleTab === tab.key ? ' lead-subtab--on' : ''}`}
              onClick={() => setConsoleTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}
      {huddleUnread !== null && huddleUnread.total > 0 ? (
        <button
          type="button"
          className="my-day-huddle-alert"
          aria-label={`${huddleCommentLabel(huddleUnread.total)}. ${huddleUnread.taskNames.join(
            ', ',
          )}. Open the newest comment.`}
          onClick={onOpenNewestHuddle}
        >
          <strong>{huddleCommentLabel(huddleUnread.total)}</strong>
          <span>{huddleUnread.taskNames.join(', ')}</span>
          <span>Latest {huddleLatestTime(huddleUnread.latestAt ?? '')}</span>
        </button>
      ) : null}
      {column.custom && huddleOpen ? (
        <HuddleLinksPanel sectionId={column.key} onLiveChange={setHuddleToken} />
      ) : null}
      {/* Delete says what happens to the work before it happens. "Delete" next
          to a list of somebody's own tasks should never be a silent verb, and
          the sentence is also the only place the ON DELETE SET NULL behaviour
          is visible to the person relying on it. */}
      {confirming ? (
        <div className="my-day-confirm" role="group" aria-label={`Delete ${label}?`}>
          <p className="my-day-confirm-text">
            Delete {label}?{' '}
            {list.openCount + list.doneCount === 0
              ? 'It is empty.'
              : `Its ${String(list.openCount + list.doneCount)} ${
                  list.openCount + list.doneCount === 1 ? 'task moves' : 'tasks move'
                } to To Do Today.`}
          </p>
          <div className="my-day-confirm-actions">
            <button type="button" className="ui-button" onClick={() => setConfirming(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="ui-button ui-button--danger"
              onClick={() => {
                setConfirming(false);
                if (onDelete !== null) onDelete();
              }}
            >
              Delete section
            </button>
          </div>
        </div>
      ) : null}
      {/* The body is the scroll container, and the head is its sibling, so a
          long list scrolls under a title that never moves. Same shape as the
          Deals Kanban columns, which is where Kyu asked for it from. */}
      <div
        className="my-day-list-body"
        onDragOver={(event) => {
          if (!accepts(event)) return;
          event.preventDefault();
          event.dataTransfer.dropEffect = 'move';
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(event) => {
          if (!accepts(event)) return;
          event.preventDefault();
          setOver(false);
          const taskId = event.dataTransfer.getData(TASK_DRAG_MIME);
          if (taskId !== '') onDrop(taskId, list.openCount);
        }}
      >
        <input
          className="my-day-add"
          type="text"
          placeholder="Add a task…"
          aria-label={`Add a task to ${label}`}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={submit}
        />
        {rows.length === 0 ? (
          <p className="my-day-empty">Nothing here. Type above, or drag something in.</p>
        ) : (
          <ul className="my-day-rows">
            {rows.map((task, index) => (
              <Row
                key={task.id}
                task={task}
                index={index}
                inList={column.key}
                columns={columns}
                canDrag={canDrag}
                onDropBefore={
                  task.is_completed || !canDrag
                    ? null
                    : (taskId) => onDrop(taskId, list.tasks.indexOf(task))
                }
                moves={
                  task.is_completed || !canDrag
                    ? null
                    : {
                        up: index > 0,
                        down: index < openShown - 1,
                        onStep: (step) => onReorder(task, step, consoleTab),
                      }
                }
                onOpen={onOpen}
                onToggle={statusPicker === null ? onToggle : onToggleHuddle}
                onMove={onMove}
                onRelease={onRelease}
                huddleUnreadCount={huddleUnread?.byTask[task.id]?.count ?? 0}
                statusPicker={statusPicker}
              />
            ))}
          </ul>
        )}
        {consoleRows === null || consoleRows.done.length === 0 ? null : (
          // Same fold and look as the page's Completed earlier (DoneEarlier
          // below). The key closes it again when the tab changes.
          <div className="my-day-done-earlier" key={consoleTab}>
            <PanelFold
              title="Completed"
              summary={String(consoleRows.done.length)}
              defaultOpen={false}
            >
              <ul className="my-day-rows">
                {consoleRows.done.map((task, index) => (
                  <Row
                    key={task.id}
                    task={task}
                    index={index}
                    inList={column.key}
                    columns={columns}
                    canDrag={false}
                    onDropBefore={null}
                    onOpen={onOpen}
                    onToggle={onToggleHuddle}
                    onMove={onMove}
                    onRelease={null}
                    huddleUnreadCount={huddleUnread?.byTask[task.id]?.count ?? 0}
                    statusPicker={statusPicker}
                  />
                ))}
              </ul>
            </PanelFold>
          </div>
        )}
      </div>
    </section>
  );
}

/* ── Add a section ──────────────────────────────────────────────────────── */

/**
 * Kyu, 7 September, on the list he removed: "there's no use for a Reminder if
 * it can't send a notification anyway. Just change it to Add a Section or
 * something. Let any user build their own section there."
 *
 * So this sits where that list used to, and looks like a list that has not
 * been named yet rather than a button somewhere else on the page.
 */
function AddSectionCard({ onAdd }: { onAdd: (name: string) => void }) {
  const [draft, setDraft] = useState('');

  function submit() {
    const name = draft.trim();
    if (name === '') return;
    onAdd(name);
    setDraft('');
  }

  return (
    <section className="my-day-list my-day-list--new" aria-label="Add a section">
      <header className="my-day-list-head">
        <h2 className="my-day-list-title">Add a section</h2>
      </header>
      <div className="my-day-list-body">
        <input
          className="my-day-add"
          type="text"
          maxLength={40}
          placeholder="Name it…"
          aria-label="Name a new section"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return;
            event.preventDefault();
            submit();
          }}
        />
        <p className="my-day-empty">
          Your own list, on your page only. Rename, reorder or remove it later.
        </p>
      </div>
    </section>
  );
}

/* ── Unfinished: read, not written ──────────────────────────────────────── */

function UnfinishedCard({
  tasks,
  columns,
  canDrag,
  onOpen,
  onToggle,
  onMove,
}: {
  tasks: Task[];
  columns: MyDayColumn[];
  canDrag: boolean;
  onOpen: (task: Task) => void;
  onToggle: (task: Task, isCompleted: boolean) => void;
  onMove: (task: Task, key: string) => void;
}) {
  return (
    <section className="my-day-list my-day-list--unfinished" aria-label={UNFINISHED_LABEL}>
      <header className="my-day-list-head">
        <h2 className="my-day-list-title">{UNFINISHED_LABEL}</h2>
        <span className="my-day-list-count">
          {tasks.length === 0 ? 'All caught up' : `${tasks.length} carried over`}
        </span>
      </header>
      {/* No drop handlers on purpose. Nothing is put here; it is what is
          left when a day ends. The way out is to drag it into a list, which
          plants today's date. */}
      <div className="my-day-list-body">
        {tasks.length === 0 ? (
          <p className="my-day-empty">Nothing carried over from earlier days.</p>
        ) : (
          <ul className="my-day-rows">
            {tasks.map((task, index) => (
              <Row
                key={task.id}
                task={task}
                index={index}
                inList={null}
                columns={columns}
                canDrag={canDrag}
                onDropBefore={null}
                onOpen={onOpen}
                onToggle={onToggle}
                onMove={onMove}
                onRelease={null}
              />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

/* ── A row ──────────────────────────────────────────────────────────────── */

function Row({
  task,
  index,
  inList,
  columns,
  canDrag: dragAllowed,
  onDropBefore,
  moves = null,
  onOpen,
  onToggle,
  onMove,
  onRelease,
  huddleUnreadCount = 0,
  statusPicker = null,
}: {
  task: Task;
  index: number;
  /** The column key this row is drawn in, or null under Unfinished. */
  inList: string | null;
  /** Every column on the page, so "Move to …" names them all. */
  columns: MyDayColumn[];
  canDrag: boolean;
  onDropBefore: ((taskId: string) => void) | null;
  /**
   * Move up, Move down, Move to top, from the menu and Alt+Up, Alt+Down,
   * Alt+Shift+Up. Null where a place means nothing or cannot be trusted:
   * Unfinished, completed rows, while searching.
   */
  moves?: { up: boolean; down: boolean; onStep: (step: Step) => void } | null;
  onOpen: (task: Task) => void;
  onToggle: (task: Task, isCompleted: boolean) => void;
  onMove: (task: Task, key: string) => void;
  onRelease: ((task: Task) => void) | null;
  huddleUnreadCount?: number;
  /** Only on rows of the TAP Console section; see huddleStatus.ts. */
  statusPicker?: {
    choices: HuddleStatusChoice[];
    statuses: ProjectStatus[];
    onSet: (task: Task, intent: HuddleStatusIntent) => void;
    onSetGroup: (task: Task, choice: ConsoleViewGroup) => void;
    /** The LasVegas option shows only while the LasVegas tab does. */
    tripEnabled: boolean;
  } | null;
}) {
  const [before, setBefore] = useState(false);
  // The TAP Console rows show a Trip task without its "Trip:" prefix, as the Console does.
  const shownTitle = statusPicker === null ? task.title : consoleShownTitle(task.title);
  const rowGroup = consoleViewGroupOf({ title: task.title, group: task.console_group });
  const titleRef = useRef<HTMLButtonElement | null>(null);
  const held = task.my_day_position !== null && inList !== null;
  const canDrag = dragAllowed && !task.is_completed;

  const items = [
    { label: 'Open', onSelect: () => onOpen(task) },
    ...(moves === null || !moves.up
      ? []
      : [
          { label: 'Move to top', onSelect: () => moves.onStep('top') },
          { label: 'Move up', onSelect: () => moves.onStep(-1) },
        ]),
    ...(moves === null || !moves.down
      ? []
      : [{ label: 'Move down', onSelect: () => moves.onStep(1) }]),
    // A finished task is not moved between lists; it is reopened or left.
    ...(task.is_completed
      ? []
      : columns
          .filter((column) => column.key !== inList)
          .map((column) => ({
            label: `Move to ${column.label}`,
            onSelect: () => onMove(task, column.key),
          }))),
    ...(held && onRelease !== null
      ? [{ label: 'Let it flow (release slot)', onSelect: () => onRelease(task) }]
      : []),
  ];

  // Keys move the row the title belongs to, so after a key move the title
  // keeps focus at its new place and the next press moves it again. The row
  // is keyed by task id, but a moved DOM node can lose focus on the way.
  const refocus = useRef(false);
  useEffect(() => {
    if (!refocus.current) return;
    refocus.current = false;
    titleRef.current?.focus();
  }, [index]);

  function onRowKeyDown(event: KeyboardEvent<HTMLLIElement>) {
    if (moves === null || !event.altKey) return;
    // Only the row itself or its title. A select opens on Alt+Down, the
    // checkbox and the row menu (overlay.tsx) have keys of their own.
    if (event.target !== event.currentTarget && event.target !== titleRef.current) return;
    const step: Step | null =
      event.key === 'ArrowDown' && !event.shiftKey
        ? 1
        : event.key === 'ArrowUp'
          ? event.shiftKey
            ? 'top'
            : -1
          : null;
    if (step === null) return;
    event.preventDefault();
    // No up/down check before the move: the flags are from the last render,
    // and a second quick press must be judged on the live order (reorder
    // does). They only decide whether the title should follow the row.
    refocus.current = step === 1 ? moves.down : moves.up;
    moves.onStep(step);
  }

  return (
    <li
      className={`my-day-row${task.is_completed ? ' my-day-row--done' : ''}${
        before ? ' my-day-row--drop-before' : ''
      }${statusPicker ? ' my-day-row--console' : ''}`}
      data-index={index}
      onKeyDown={moves === null ? undefined : onRowKeyDown}
      onDragOver={
        onDropBefore === null
          ? undefined
          : (event) => {
              if (!event.dataTransfer.types.includes(TASK_DRAG_MIME)) return;
              event.preventDefault();
              event.dataTransfer.dropEffect = 'move';
              setBefore(true);
            }
      }
      onDragLeave={() => setBefore(false)}
      onDrop={
        onDropBefore === null
          ? undefined
          : (event) => {
              if (!event.dataTransfer.types.includes(TASK_DRAG_MIME)) return;
              event.preventDefault();
              // The list body would otherwise also take it and append.
              event.stopPropagation();
              setBefore(false);
              const taskId = event.dataTransfer.getData(TASK_DRAG_MIME);
              if (taskId !== '') onDropBefore(taskId);
            }
      }
    >
      <span
        className="my-day-grip"
        draggable={canDrag}
        role="button"
        tabIndex={-1}
        aria-label={`Move ${task.title}`}
        title="Drag to place"
        onDragStart={(event) => {
          event.dataTransfer.setData(TASK_DRAG_MIME, task.id);
          event.dataTransfer.effectAllowed = 'move';
        }}
      >
        ⋮⋮
      </span>
      <input
        type="checkbox"
        className="my-day-check"
        checked={task.is_completed}
        aria-label={task.is_completed ? `Reopen ${task.title}` : `Complete ${task.title}`}
        onChange={() => onToggle(task, !task.is_completed)}
      />
      <button ref={titleRef} type="button" className="my-day-title" onClick={() => onOpen(task)}>
        {shownTitle}
      </button>
      {statusPicker === null ? null : (
        /* A native select: the phone draws its own full-screen picker, the
           keyboard works without extra code, and a disabled choice can say
           why. Same three words Alex reads, in the same order. */
        <select
          className="my-day-status"
          aria-label={`Status for ${task.title}`}
          value={huddleStatusOf(task, statusPicker.statuses)}
          onChange={(event) => {
            const choice = statusPicker.choices.find((item) => item.key === event.target.value);
            if (choice?.intent) statusPicker.onSet(task, choice.intent);
          }}
        >
          {statusPicker.choices.map((choice) => (
            <option
              key={choice.key}
              value={choice.key}
              disabled={choice.intent === null}
              title={choice.reason === '' ? undefined : choice.reason}
            >
              {choice.label}
            </option>
          ))}
        </select>
      )}
      {statusPicker === null ? null : (
        /* The group Alex's tabs read (Kyu, 30 Sept). Same small native pill
           as the status picker beside it; untagged reads as Others. */
        <select
          className="my-day-status my-day-group"
          aria-label={`Group for ${shownTitle}`}
          value={rowGroup}
          onChange={(event) =>
            statusPicker.onSetGroup(task, event.target.value as ConsoleViewGroup)
          }
        >
          {CONSOLE_GROUP_ORDER.map((key) => (
            <option key={key} value={key}>
              {CONSOLE_GROUP_LABELS[key]}
            </option>
          ))}
          <option value="parked">{PARKED_LABEL}</option>
          {statusPicker.tripEnabled || rowGroup === 'trip' ? (
            <option value="trip">{TRIP_LABEL}</option>
          ) : null}
        </select>
      )}
      <span className="my-day-meta">
        {huddleUnreadCount > 0 ? (
          <span className="my-day-huddle-new">
            <span className="my-day-huddle-dot" aria-hidden="true" />
            <span>({huddleUnreadCount} new from Alex)</span>
          </span>
        ) : null}
        {held ? (
          <span className="my-day-held" title="Placed by hand; stays where you put it">
            <span aria-hidden="true">📌</span>
            <span className="ui-visually-hidden">held in place</span>
          </span>
        ) : null}
        {task.priority === null ? null : (
          <span className={`my-day-priority my-day-priority--${task.priority}`}>
            {TASK_PRIORITY_LABELS[task.priority]}
          </span>
        )}
        {task.is_completed && inList === null ? (
          <span className="my-day-planned">done {formatDate(completedOn(task))}</span>
        ) : inList === null ? (
          <span className="my-day-planned">planned {formatDate(plannedDay(task))}</span>
        ) : task.due_date === null ? null : (
          <span className="my-day-due">due {formatDate(task.due_date)}</span>
        )}
      </span>
      <Menu
        trigger={<span aria-hidden="true">…</span>}
        triggerLabel={`Actions for ${task.title}`}
        items={items}
      />
    </li>
  );
}

/* ── Completed before today ─────────────────────────────────────────────── */

function DoneEarlier({
  doneEarlier,
  columns,
  onOpen,
  onToggle,
}: {
  doneEarlier: Task[];
  columns: MyDayColumn[];
  onOpen: (task: Task) => void;
  onToggle: (task: Task, isCompleted: boolean) => void;
}) {
  if (doneEarlier.length === 0) return null;
  return (
    <div className="my-day-done-earlier">
      <PanelFold title="Completed earlier" summary={String(doneEarlier.length)} defaultOpen={false}>
        <ul className="my-day-rows">
          {doneEarlier.map((task, index) => (
            <Row
              key={task.id}
              task={task}
              index={index}
              inList={null}
              columns={columns}
              canDrag={false}
              onDropBefore={null}
              onOpen={onOpen}
              onToggle={onToggle}
              // Never called: every row here is completed, and the menu
              // offers no move for a completed task.
              onMove={() => undefined}
              onRelease={null}
            />
          ))}
        </ul>
      </PanelFold>
    </div>
  );
}
